/**
 * PDF → Seiten für den Altplan-Leser (altplanLesen.ts).
 *
 * pdf.js liefert zwei Dinge, die der Leser braucht: die Textstücke mit Lage
 * (getTextContent) und die Zellrahmen. Die Rahmen stehen nicht als Linien im
 * Text, sondern in der Operatorliste — die Excel-Vorlage zeichnet sie als
 * schmale gefüllte Rechtecke, jeweils unter einer eigenen Transformation.
 * Deshalb läuft hier ein kleiner Transformationsstapel mit
 * (save/restore/transform, Form-XObjects), sonst lägen die Rahmen im
 * falschen Maßstab neben dem Text.
 *
 * Beide Umrechnungen sind reine Funktionen und ohne pdf.js testbar; nur
 * `leseAltplanSeiten` lädt die Bibliothek (dynamisch, wie NativePdfSeiten).
 */

import type { PDFPageProxy } from 'pdfjs-dist';
import type { AltplanLinie, AltplanSeite, AltplanText } from './altplanLesen';

/** Mehr Seiten hat kein Plan; ein versehentlich gewähltes Handbuch soll nicht minutenlang laufen. */
export const ALTPLAN_MAX_SEITEN = 12;

type Matrix = [number, number, number, number, number, number];

/** Matrixprodukt m × n in der Schreibweise von pdf.js (Util.transform). */
const verkette = (m: Matrix, n: ArrayLike<number>): Matrix => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];

const punkt = (m: Matrix, x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];

/** Private-Use-Zeichen (Wingdings-Aufzählungspunkte) tragen keinen Inhalt. */
const ohneSymbole = (s: string): string => s.replace(/[-]/g, '');

/** Die Operator-Kennungen, die die Umrechnung braucht (pdf.js `OPS`). */
export interface AltplanOps {
  save: number;
  restore: number;
  transform: number;
  constructPath: number;
  paintFormXObjectBegin: number;
  paintFormXObjectEnd: number;
}

/**
 * Schmale Rechtecke und Linien der Operatorliste in Seitenkoordinaten
 * (pt, Ursprung oben links). Gelesen wird nur der Begrenzungsrahmen jedes
 * Pfads (`minMax`, drittes Argument von constructPath) — er reicht für
 * Zellrahmen und ist zwischen den pdf.js-Versionen stabil. Alles, was in
 * beiden Richtungen breiter als 3 pt ist (Balken, Hinterlegungen), fällt weg.
 */
export const linienAusOperatoren = (
  fnArray: ArrayLike<number>,
  argsArray: ArrayLike<unknown>,
  ops: AltplanOps,
  seitenMatrix: ArrayLike<number>,
): AltplanLinie[] => {
  let ctm: Matrix = [seitenMatrix[0], seitenMatrix[1], seitenMatrix[2], seitenMatrix[3], seitenMatrix[4], seitenMatrix[5]];
  const stapel: Matrix[] = [];
  const aus: AltplanLinie[] = [];
  for (let i = 0; i < fnArray.length; i++) {
    const fn = fnArray[i];
    const args = argsArray[i] as ArrayLike<unknown> | null | undefined;
    if (fn === ops.save) stapel.push(ctm);
    else if (fn === ops.restore) ctm = stapel.pop() ?? ctm;
    else if (fn === ops.transform && args) ctm = verkette(ctm, args as ArrayLike<number>);
    else if (fn === ops.paintFormXObjectBegin) {
      stapel.push(ctm);
      const matrix = args?.[0] as ArrayLike<number> | null | undefined;
      if (matrix && matrix.length === 6) ctm = verkette(ctm, matrix);
    } else if (fn === ops.paintFormXObjectEnd) ctm = stapel.pop() ?? ctm;
    else if (fn === ops.constructPath && args) {
      const mm = args[2] as ArrayLike<number> | null | undefined;
      if (!mm || mm.length < 4 || ![mm[0], mm[1], mm[2], mm[3]].every(Number.isFinite)) continue;
      const [ax, ay] = punkt(ctm, mm[0], mm[1]);
      const [bx, by] = punkt(ctm, mm[2], mm[3]);
      const linie = { x0: Math.min(ax, bx), y0: Math.min(ay, by), x1: Math.max(ax, bx), y1: Math.max(ay, by) };
      const w = linie.x1 - linie.x0;
      const h = linie.y1 - linie.y0;
      if (Math.min(w, h) <= 3 && Math.max(w, h) >= 4) aus.push(linie);
    }
  }
  return aus;
};

/** Ein Eintrag aus getTextContent, so weit er hier gelesen wird. */
export interface PdfTextEintrag {
  str?: string;
  transform?: ArrayLike<number>;
  width?: number;
}

/**
 * Textstücke in Seitenkoordinaten. Gedrehter Text (senkrechte Beschriftung)
 * fällt weg — die Vorlage hat keinen, und er stünde sonst quer in einer
 * Spalte.
 */
export const texteAusInhalt = (eintraege: ArrayLike<PdfTextEintrag>, seitenMatrix: ArrayLike<number>): AltplanText[] => {
  const seite: Matrix = [seitenMatrix[0], seitenMatrix[1], seitenMatrix[2], seitenMatrix[3], seitenMatrix[4], seitenMatrix[5]];
  const aus: AltplanText[] = [];
  for (let i = 0; i < eintraege.length; i++) {
    const e = eintraege[i];
    if (typeof e?.str !== 'string' || !e.transform) continue;
    const text = ohneSymbole(e.str);
    if (!text.trim()) continue;
    const m = verkette(seite, e.transform);
    if (Math.abs(m[1]) > 0.1 * Math.abs(m[0])) continue;
    const skala = Math.hypot(seite[0], seite[1]) || 1;
    aus.push({ x: m[4], y: m[5], breite: (e.width || 0) * skala, hoehe: Math.hypot(m[2], m[3]), text });
  }
  return aus;
};

/** Eine geladene Seite → Eingabe für den Leser. */
export const seiteAusPdf = async (seite: PDFPageProxy, ops: AltplanOps): Promise<AltplanSeite> => {
  const vp = seite.getViewport({ scale: 1 });
  const [inhalt, liste] = await Promise.all([seite.getTextContent(), seite.getOperatorList()]);
  return {
    breite: vp.width,
    hoehe: vp.height,
    texte: texteAusInhalt(inhalt.items as PdfTextEintrag[], vp.transform),
    linien: linienAusOperatoren(liste.fnArray, liste.argsArray, ops, vp.transform),
  };
};

/** PDF-Datei → Seiten (höchstens ALTPLAN_MAX_SEITEN). */
export const leseAltplanSeiten = async (daten: ArrayBuffer): Promise<{ seiten: AltplanSeite[]; seitenGesamt: number }> => {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  // Worker aus dem Bundle wie in NativePdfSeiten — nur setzen, falls noch leer.
  if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = worker.default as string;
  const ladeauftrag = pdfjs.getDocument({ data: new Uint8Array(daten) });
  try {
    const dok = await ladeauftrag.promise;
    const seiten: AltplanSeite[] = [];
    for (let nr = 1; nr <= Math.min(dok.numPages, ALTPLAN_MAX_SEITEN); nr++) {
      seiten.push(await seiteAusPdf(await dok.getPage(nr), pdfjs.OPS));
    }
    return { seiten, seitenGesamt: dok.numPages };
  } finally {
    // Der Ladeauftrag hält den Worker — ihn zerstören, nicht nur das Dokument.
    void ladeauftrag.destroy();
  }
};
